import { all, takeEvery, put, call, fork } from 'redux-saga/effects';
import firebase from 'firebase';
import actions from './actions';
import { establishSession, clearSession } from './session';
import { friendlyAuthMessage, callableCode } from './authErrors';
import { callGoogleSignIn } from '../../components/library/google/clientAccess';

// Password login. Same continuation as Google (establishSession), so both behave identically.
export function* loginWorker({ payload }) {
  const auth = firebase.auth();
  try {
    const { credentials } = payload;
    const result = yield call([auth, auth.signInWithEmailAndPassword], credentials.email, credentials.password);
    const session = yield call(establishSession, result.user);
    yield put(actions.loginSuccess(session));
  } catch (error) {
    yield call(abortSession, auth);
    yield put(actions.loginError({ code: error.code, message: friendlyAuthMessage(error) }));
  }
}

// Google login: our own callable verifies the Google token and mints a Firebase custom token for the
// EXISTING account. It never creates users; an unknown e-mail opens the "not found" modal.
export function* googleLoginWorker({ payload }) {
  const auth = firebase.auth();
  const { accessToken } = payload;
  const profile = payload.profile || {};
  try {
    const data = yield call(callGoogleSignIn, accessToken, profile.picture);
    const result = yield call([auth, auth.signInWithCustomToken], data.token);
    const photo = data.picture || profile.picture || '';
    const session = yield call(establishSession, result.user, { photo });
    yield put(actions.loginSuccess(session));
  } catch (error) {
    yield call(abortSession, auth);
    if (callableCode(error) === 'not-found') {
      yield put(actions.accessNotFound({ email: profile.email || '', name: profile.name || '' }));
    } else {
      yield put(actions.loginError({ code: error.code, message: friendlyAuthMessage(error) }));
    }
  }
}

// A failed login must leave nothing behind: no half-open Firebase session, no stale keys.
export function* abortSession(auth) {
  clearSession();
  try {
    yield call([auth, auth.signOut]);
  } catch (e) {
    // nothing to sign out of
  }
}

export function* loginRequest() {
  yield takeEvery('LOGIN_REQUEST', loginWorker);
}

export function* googleLoginRequest() {
  yield takeEvery(actions.GOOGLE_LOGIN_REQUEST, googleLoginWorker);
}

export function* loginSuccess() {
  yield takeEvery(actions.LOGIN_SUCCESS, function*(payload) {
    yield localStorage.setItem('id_token', payload.token);
  });
}

export function* loginError() {
  yield takeEvery(actions.LOGIN_ERROR, function*() {});
}

export function* resetPasswordRequest() {
  yield takeEvery('RESET_PASSWORD_REQUEST', function*({ payload }) {
    try {
      const { credentials } = payload;
      const auth = firebase.auth()
      yield call([auth, auth.sendPasswordResetEmail], credentials.email)
      yield put(actions.resetPasswordSuccess({ message: 'An email has been sent to your account to reset your password'}));
    } catch (error) {
      const error_message = { code: error.code, message: error.message };
      yield put(actions.resetPasswordError(error_message));
    }
  });
}

export function* resetPasswordSuccess() {
  yield takeEvery(actions.RESET_PASSWORD_SUCCESS, function*(payload) {
  yield localStorage.setItem('id_token', payload.token);
  });
}

export function* resetPasswordError() {
  yield takeEvery(actions.RESET_PASSWORD_ERROR, function*() {});
}

export function* resetPasswordConfirm() {
  yield takeEvery('RESET_PASSWORD_CONFIRM', function*({ payload }) {
    try {
      const { credentials } = payload;
      const auth = firebase.auth();
      yield call([auth, auth.confirmPasswordReset], credentials.oobCode, credentials.password)
      yield put(actions.resetPasswordConfirmSuccess({ message: 'Your password has been modified correctly'}));
    } catch (error) {
      const error_message = { code: error.code, message: error.message };
      yield put(actions.resetPasswordConfirmError(error_message));
    }
  });
}

export function* logout() {
  const auth = firebase.auth();
  yield call(abortSession, auth);
  yield put(actions.logoutSuccess());
}

export default function* rootSaga() {
  yield all([
    fork(loginRequest),
    fork(googleLoginRequest),
    fork(resetPasswordRequest),
    fork(resetPasswordConfirm),
    takeEvery(actions.LOGOUT, logout),
  ]);
}
